import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import LoadingSpinner from "@/components/LoadingSpinner";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { BackToIntegrations } from "../shared/IntegrationPageWrapper";

// ─── Types ────────────────────────────────────────────────────────────────────

interface RecruitingConfig {
  jobsFeedUrl: string;
  feedType: string;
  recruiterEmail: string;
  companyName: string;
  companyDescription: string;
  isEnabled: boolean;
  lastSyncAt: string | null;
  activeJobCount: number;
  atsType?: string;
  atsApiKey?: string;
  atsBoardToken?: string;
  atsJobIdOverride?: string;
}

interface JobListing {
  id: number;
  title: string;
  department: string;
  location: string;
  employmentType: string;
  applyUrl: string;
  isActive: boolean;
  updatedAt: string;
}

interface CandidateApplication {
  id: number;
  candidateName: string;
  email: string;
  phone: string;
  roleInterest: string;
  workAuthorization: string;
  availability: string;
  notes: string;
  conversationId: string;
  submittedAt: string;
  // EEOC voluntary self-ID — null when module not enabled or candidate skipped
  eeocRaceEthnicity?: string | null;
  eeocGender?: string | null;
  eeocVeteranStatus?: string | null;
  eeocDisabilityStatus?: string | null;
  eeocOfferedAt?: string | null;
}

interface SyncResult {
  success: boolean;
  error?: string;
  added?: number;
  updated?: number;
  total?: number;
}

// ─── Feed type options ────────────────────────────────────────────────────────

const FEED_TYPES = [
  { value: "auto",           label: "Auto-detect" },
  { value: "json",           label: "JSON feed" },
  { value: "xml",            label: "XML / RSS feed" },
  { value: "successfactors", label: "SAP SuccessFactors OData" },
  { value: "workday",        label: "Workday" },
];

// ─── Tab type ─────────────────────────────────────────────────────────────────

type Tab = "setup" | "howitworks" | "jobs" | "applications" | "log";

// ─── Exports ─────────────────────────────────────────────────────────────────

export function BackToIntegrationLandingRecruiting() {
  return <BackToIntegrations />;
}

// ─── How It Works panel ───────────────────────────────────────────────────────

function HowItWorks() {
  return (
    <div className="space-y-6">

      {/* Architecture overview */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-semibold text-slate-800 mb-3">How the Recruiting Bot Works</h2>
        <p className="text-sm text-slate-600 mb-4">
          The bot does <strong>not</strong> query your ATS live during a conversation. Instead, Velaro
          periodically imports your job listings into its own database so the bot can answer questions
          instantly. This is the same architecture used by dedicated recruiting AI platforms.
        </p>
        <div className="space-y-3">
          {[
            { n: "1", title: "Jobs Synced In", body: "Velaro reads your jobs feed URL and stores listings in its database. Hit Sync Now any time, or enable scheduled sync for fully automatic updates." },
            { n: "2", title: "Candidate Chats", body: "When a visitor starts a chat, the bot greets them, searches your synced listings for matching roles, surfaces apply links, answers culture and process questions, and collects: name, email, phone, work authorization, and availability." },
            { n: "3", title: "Recruiter Gets Notified", body: "When a candidate submits their info, Velaro emails a summary to your configured recruiter address with all collected details and a link to the full conversation transcript." },
          ].map(step => (
            <div key={step.n} className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                {step.n}
              </div>
              <div>
                <div className="font-medium text-slate-700 text-sm">{step.title}</div>
                <div className="text-sm text-slate-500 mt-0.5">{step.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Three methods */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-semibold text-slate-800 mb-1">Three Ways to Connect Your Jobs</h2>
        <p className="text-sm text-slate-500 mb-4">Choose the method that fits your setup.</p>

        <div className="space-y-4">
          <div className="border border-green-200 bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold bg-green-600 text-white px-2 py-0.5 rounded">RECOMMENDED</span>
              <span className="font-medium text-slate-800 text-sm">Public XML or JSON Feed — No Credentials Required</span>
            </div>
            <p className="text-sm text-slate-600 mb-2">
              Most ATS platforms publish a public feed for job boards like Indeed and Google Jobs.
              This is the easiest setup — just paste the URL.
            </p>
            <div className="text-xs text-slate-500 space-y-0.5">
              <div><strong>Greenhouse:</strong> <code className="bg-white px-1 rounded border">https://boards.greenhouse.io/yourcompany/jobs.json</code></div>
              <div><strong>Lever:</strong> <code className="bg-white px-1 rounded border">https://api.lever.co/v0/postings/yourcompany?mode=json</code></div>
              <div><strong>Workday:</strong> <code className="bg-white px-1 rounded border">https://yourcompany.wd5.myworkdayjobs.com/careers</code></div>
              <div><strong>iCIMS:</strong> <code className="bg-white px-1 rounded border">https://careers.yourcompany.com/jobs/feed.rss</code></div>
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg p-4">
            <div className="font-medium text-slate-800 text-sm mb-1">Private ATS API — Full Integration</div>
            <p className="text-sm text-slate-600">
              For deeper access, connect directly to your ATS's private API. Supports SAP SuccessFactors
              OData v4, Workday REST API, and any OAuth JSON feed. Your credentials are stored securely
              and only used for the scheduled sync job — never exposed in conversations.
            </p>
          </div>

          <div className="border border-slate-200 rounded-lg p-4">
            <div className="font-medium text-slate-800 text-sm mb-1">Manual Job Listings — No ATS Required</div>
            <p className="text-sm text-slate-600">
              For small teams or when you want to feature only specific roles, enter jobs directly in
              the Job Listings tab. No sync required — the bot reads them immediately.
            </p>
          </div>
        </div>
      </div>

      {/* Deploy section */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-semibold text-slate-800 mb-3">Deploying the Bot</h2>
        <div className="space-y-3 text-sm text-slate-600">
          <p>After configuring your feed and running the Bot Wizard, deploy in three steps:</p>
          <ol className="list-decimal list-inside space-y-2 ml-1">
            <li><strong>Assign the workflow</strong> — In <span className="font-mono text-xs bg-slate-100 px-1 rounded">Bots → AI Configuration</span>, select your recruiting bot and assign the workflow the wizard created.</li>
            <li><strong>Create a deployment</strong> — In <span className="font-mono text-xs bg-slate-100 px-1 rounded">Deployments</span>, create a new chat button and embed the snippet on your careers page.</li>
            <li><strong>Add a routing rule (recommended)</strong> — In <span className="font-mono text-xs bg-slate-100 px-1 rounded">Rules</span>, create a rule: <em>If Page URL contains /careers → Route to Recruiting Bot</em>. This way your recruiting bot only activates on the careers page, and your regular support bot handles everything else.</li>
          </ol>
        </div>
      </div>

      {/* Email flow */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-semibold text-slate-800 mb-3">Where Do Emails Go?</h2>
        <p className="text-sm text-slate-600 mb-3">
          When a candidate completes the intake flow, Velaro sends a notification email to the
          <strong> Recruiter Email</strong> address you configure in the Setup tab. The email includes:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 ml-1">
          <li>Candidate name, email, and phone number</li>
          <li>Role they expressed interest in</li>
          <li>Work authorization status</li>
          <li>Availability / start date preference</li>
          <li>Any notes or free-text they provided</li>
          <li>Link to the full conversation transcript</li>
        </ul>
        <p className="text-sm text-slate-500 mt-3">
          The bot workflow handles all question-asking automatically — the recruiter receives
          a clean summary without ever having to monitor the chat.
        </p>
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Recruiting() {
  const api      = useApi();
  const navigate = useNavigate();
  const { subscription } = useSubscriptions();
  const eeocEnabled = !!subscription?.enableRecruitingBotEeoc;

  const [tab,           setTab]           = useState<Tab>("setup");
  const [config,        setConfig]        = useState<RecruitingConfig | null>(null);
  const [jobs,          setJobs]          = useState<JobListing[]>([]);
  const [applications,  setApplications]  = useState<CandidateApplication[]>([]);
  const [appsLoading,   setAppsLoading]   = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [syncing,       setSyncing]       = useState(false);
  const [saveResult,    setSaveResult]    = useState<{ ok: boolean; msg: string } | null>(null);
  const [syncResult,    setSyncResult]    = useState<SyncResult | null>(null);
  const [showWizard,    setShowWizard]    = useState(false);
  const [jobSearch,     setJobSearch]     = useState("");

  // ── Form state ──────────────────────────────────────────────────────────────
  const [feedUrl,          setFeedUrl]          = useState("");
  const [feedType,         setFeedType]         = useState("auto");
  const [recruiterEmail,   setRecruiterEmail]   = useState("");
  const [companyName,      setCompanyName]      = useState("");
  const [companyDesc,      setCompanyDesc]      = useState("");
  const [atsType,          setAtsType]          = useState("");
  const [atsApiKey,        setAtsApiKey]        = useState("");
  const [atsBoardToken,    setAtsBoardToken]    = useState("");
  const [atsJobIdOverride, setAtsJobIdOverride] = useState("");

  // ── Load config ─────────────────────────────────────────────────────────────

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await api.messaging.get("Recruiting/Config");
      const data = await res.json() as RecruitingConfig;
      setConfig(data);
      setFeedUrl(data.jobsFeedUrl ?? "");
      setFeedType(data.feedType ?? "auto");
      setRecruiterEmail(data.recruiterEmail ?? "");
      setCompanyName(data.companyName ?? "");
      setCompanyDesc(data.companyDescription ?? "");
      setAtsType(data.atsType ?? "");
      setAtsApiKey(data.atsApiKey ?? "");
      setAtsBoardToken(data.atsBoardToken ?? "");
      setAtsJobIdOverride(data.atsJobIdOverride ?? "");
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadJobs = useCallback(async () => {
    try {
      const res  = await api.messaging.get(`Recruiting/Jobs${jobSearch ? `?search=${encodeURIComponent(jobSearch)}` : ""}`);
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : []);
    } catch {
      setJobs([]);
    }
  }, [api, jobSearch]);

  const loadApplications = useCallback(async () => {
    setAppsLoading(true);
    try {
      const res  = await api.messaging.get("Recruiting/Applications");
      const data = await res.json();
      setApplications(Array.isArray(data) ? data : []);
    } catch {
      setApplications([]);
    } finally {
      setAppsLoading(false);
    }
  }, [api]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { if (tab === "jobs") loadJobs(); }, [tab, loadJobs]);
  useEffect(() => { if (tab === "applications") loadApplications(); }, [tab, loadApplications]);

  // ── Save config ─────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      const res = await api.messaging.put("Recruiting/Config", {
        jobsFeedUrl:        feedUrl.trim(),
        feedType,
        recruiterEmail:     recruiterEmail.trim(),
        companyName:        companyName.trim(),
        companyDescription: companyDesc.trim(),
        isEnabled:          true,
        atsType:            atsType.trim() || null,
        atsApiKey:          atsApiKey.trim() || null,
        atsBoardToken:      atsBoardToken.trim() || null,
        atsJobIdOverride:   atsJobIdOverride.trim() || null,
      });
      if (res.ok) {
        setSaveResult({ ok: true, msg: "Configuration saved." });
        await loadConfig();
      } else {
        setSaveResult({ ok: false, msg: "Save failed." });
      }
    } catch {
      setSaveResult({ ok: false, msg: "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  // ── Sync jobs ────────────────────────────────────────────────────────────────

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res  = await api.messaging.post("Recruiting/Sync", {});
      const data = await res.json() as SyncResult;
      setSyncResult(data);
      if (data.success) { await loadConfig(); await loadJobs(); }
    } catch {
      setSyncResult({ success: false, error: "Sync request failed." });
    } finally {
      setSyncing(false);
    }
  };

  // ── Delete job ───────────────────────────────────────────────────────────────

  const handleDeleteJob = async (id: number) => {
    try {
      await api.messaging.delete(`Recruiting/Jobs/${id}`);
      setJobs(prev => prev.filter(j => j.id !== id));
    } catch { /* non-fatal */ }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl">

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Recruiting Bot</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Connect your careers feed so the AI can surface open roles, answer candidate questions,
            and send qualified leads directly to your recruiter.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/bots")}
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50"
            title="Go to Bots to assign the workflow and set up a deployment"
          >
            <i className="fa fa-rocket" />
            Deploy Bot
          </button>
          <button
            onClick={() => setShowWizard(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
          >
            <i className="fa fa-wand-magic-sparkles" />
            Launch Bot Wizard
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {config && (
        <div className="flex gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex-1 text-center">
            <div className="text-2xl font-bold text-slate-800">{config.activeJobCount}</div>
            <div className="text-xs text-slate-500 mt-0.5">Active Listings</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex-1 text-center">
            <div className="text-sm font-medium text-slate-700">
              {config.lastSyncAt ? new Date(config.lastSyncAt).toLocaleString() : "Never synced"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Last Sync</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex-1 text-center">
            <div className={`text-sm font-medium ${config.isEnabled ? "text-green-600" : "text-slate-400"}`}>
              {config.isEnabled ? "Active" : "Inactive"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Status</div>
          </div>
        </div>
      )}

      {/* Deploy checklist banner — shown when config is saved but no jobs synced yet */}
      {config && config.activeJobCount === 0 && feedUrl && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
          <div className="font-semibold text-amber-800 mb-2">Setup Checklist</div>
          <ol className="space-y-1 text-amber-700 list-decimal list-inside">
            <li className={feedUrl ? "line-through text-amber-400" : ""}>Enter your jobs feed URL and save</li>
            <li className={config.activeJobCount > 0 ? "line-through text-amber-400" : ""}>Click Sync Now to import listings</li>
            <li>Launch Bot Wizard to create the conversation workflow</li>
            <li>Go to <button className="underline font-medium" onClick={() => navigate("/bots")}>Bots</button> → assign workflow → create deployment</li>
            <li>Add a routing rule: <em>Page URL contains /careers → Route to Recruiting Bot</em></li>
          </ol>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-1">
          {([
            { key: "setup",       label: "Setup" },
            { key: "howitworks", label: "How It Works" },
            { key: "jobs",        label: "Job Listings" },
            { key: "applications",label: "Applications" },
            { key: "log",         label: "Activity Log" },
          ] as { key: Tab; label: string }[]).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Setup tab ── */}
      {tab === "setup" && (
        <div className="space-y-6">

          {/* Jobs feed */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h2 className="font-semibold text-slate-700">Jobs Feed</h2>
            <p className="text-sm text-slate-500">
              Paste your careers page URL or ATS feed URL. Velaro will sync open positions
              and make them searchable by the recruiting bot.
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Jobs Feed URL</label>
              <input
                type="url"
                value={feedUrl}
                onChange={e => setFeedUrl(e.target.value)}
                placeholder="https://careers.yourcompany.com/jobs.json"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-slate-400 mt-1">
                Supports: JSON arrays · XML/RSS · SAP SuccessFactors OData · Workday · Indeed XML
              </p>
            </div>

            <div className="max-w-xs">
              <label className="block text-sm font-medium text-slate-700 mb-1">Feed Format</label>
              <select
                value={feedType}
                onChange={e => setFeedType(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {FEED_TYPES.map(ft => (
                  <option key={ft.value} value={ft.value}>{ft.label}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={handleSync}
                disabled={syncing || !feedUrl.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900 disabled:opacity-50"
              >
                {syncing ? <><LoadingSpinner /> Syncing…</> : <><i className="fa fa-rotate" /> Sync Now</>}
              </button>
            </div>

            {syncResult && (
              <div className={`rounded-lg px-4 py-3 text-sm ${syncResult.success ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
                {syncResult.success
                  ? `✓ Synced ${syncResult.total} jobs — ${syncResult.added} added, ${syncResult.updated} updated`
                  : `✗ ${syncResult.error}`}
              </div>
            )}
          </div>

          {/* Recruiter & company */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h2 className="font-semibold text-slate-700">Recruiter Notifications</h2>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Recruiter Email</label>
              <input
                type="email"
                value={recruiterEmail}
                onChange={e => setRecruiterEmail(e.target.value)}
                placeholder="recruiter@yourcompany.com"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-slate-400 mt-1">
                When a candidate submits their info via the bot, this address receives a summary email with
                their details and a link to the conversation transcript.
              </p>
            </div>
          </div>

          {/* Company branding */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h2 className="font-semibold text-slate-700">Company Branding</h2>
            <p className="text-sm text-slate-500">
              The bot uses this text to encourage candidates and answer "why work here?" questions.
              The more detail you provide — culture, benefits, mission, growth — the better the bot performs.
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="Acme Corp"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Company Description</label>
              <textarea
                rows={4}
                value={companyDesc}
                onChange={e => setCompanyDesc(e.target.value)}
                placeholder="2–4 sentences about what makes your company a great place to work — culture, benefits, mission, growth opportunities..."
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
              <p className="text-xs text-slate-400 mt-1">{companyDesc.length} chars</p>
            </div>
          </div>

          {/* ATS Write-Back */}
          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">ATS Write-Back <span className="text-xs font-normal text-slate-400 ml-2">Optional — push candidates to your ATS automatically</span></h3>
            <p className="text-xs text-slate-500 mb-4">When a candidate completes the intake bot, their profile is automatically submitted to your ATS. Supported: Greenhouse, Lever.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">ATS System</label>
                <select
                  value={atsType}
                  onChange={e => setAtsType(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Disabled — store in Velaro only</option>
                  <option value="greenhouse">Greenhouse</option>
                  <option value="lever">Lever</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {atsType === "greenhouse" ? "Greenhouse Job Board API Key" : atsType === "lever" ? "Lever Partner API Key" : "API Key"}
                </label>
                <input
                  type="password"
                  value={atsApiKey}
                  onChange={e => setAtsApiKey(e.target.value)}
                  placeholder={atsType === "greenhouse" ? "Your Greenhouse Job Board API key" : atsType === "lever" ? "Your Lever Partner API key" : "API key"}
                  disabled={!atsType}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-slate-50 disabled:text-slate-400"
                />
              </div>
              {atsType === "greenhouse" && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Greenhouse Board Token <span className="text-slate-400">(the slug in your job board URL)</span></label>
                  <input
                    type="text"
                    value={atsBoardToken}
                    onChange={e => setAtsBoardToken(e.target.value)}
                    placeholder="e.g. timken (from boards.greenhouse.io/timken)"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}
              {atsType && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Default Job ID Override <span className="text-slate-400">(optional — applies when no job is matched)</span></label>
                  <input
                    type="text"
                    value={atsJobIdOverride}
                    onChange={e => setAtsJobIdOverride(e.target.value)}
                    placeholder="ATS job or posting ID"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Save */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Configuration"}
            </button>
            {saveResult && (
              <span className={`text-sm ${saveResult.ok ? "text-green-600" : "text-red-600"}`}>
                {saveResult.msg}
              </span>
            )}
          </div>

          {/* ATS instructions */}
          <details className="bg-slate-50 border border-slate-200 rounded-xl">
            <summary className="px-5 py-3 cursor-pointer text-sm font-medium text-slate-700 select-none">
              ATS-Specific Setup Instructions
            </summary>
            <div className="px-5 pb-4 space-y-3 text-sm text-slate-600">
              <div>
                <strong>SAP SuccessFactors:</strong> Use your OData endpoint —
                <code className="ml-1 text-xs bg-slate-100 px-1 rounded">https://api8.successfactors.com/odata/v4/Job_Requisition</code>.
                Set Feed Format to "SAP SuccessFactors OData". Your SF admin must grant API access.
                For demos, use the public careers site sitemap instead (no credentials required).
              </div>
              <div>
                <strong>Workday:</strong> Use your public careers URL —
                <code className="ml-1 text-xs bg-slate-100 px-1 rounded">https://company.wd5.myworkdayjobs.com/careers</code>.
                Set Feed Format to "Workday". No API key required for public listings.
              </div>
              <div>
                <strong>Greenhouse / Lever / iCIMS:</strong> Look for a JSON job board feed endpoint
                in your ATS settings — usually under "Job Board API" or "Career Site". Select "JSON feed".
              </div>
              <div>
                <strong>Indeed / RSS:</strong> Most ATS platforms publish an XML feed compatible with Indeed.
                Use the XML feed URL directly and select "XML / RSS feed".
              </div>
              <div>
                <strong>Custom JSON:</strong> Any URL returning a JSON array of objects with
                <code className="ml-1 text-xs bg-slate-100 px-1 rounded">title</code>,
                <code className="ml-1 text-xs bg-slate-100 px-1 rounded">location</code>, and
                <code className="ml-1 text-xs bg-slate-100 px-1 rounded">url</code> fields works out of the box.
              </div>
            </div>
          </details>
        </div>
      )}

      {/* ── How It Works tab ── */}
      {tab === "howitworks" && <HowItWorks />}

      {/* ── Jobs tab ── */}
      {tab === "jobs" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={jobSearch}
              onChange={e => setJobSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && loadJobs()}
              placeholder="Search titles or descriptions…"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={loadJobs}
              className="px-4 py-2 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-900"
            >
              Search
            </button>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <i className="fa fa-rotate" />
              {syncing ? "Syncing…" : "Sync"}
            </button>
          </div>

          {jobs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <i className="fa fa-briefcase text-3xl mb-3 block" />
              <p className="text-sm">No job listings yet. Add your jobs feed URL and click Sync.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {jobs.map(job => (
                <div key={job.id} className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{job.title}</div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {job.department && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{job.department}</span>}
                      {job.location   && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{job.location}</span>}
                      {job.employmentType && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">{job.employmentType}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {job.applyUrl && (
                      <a href={job.applyUrl} target="_blank" rel="noreferrer"
                        className="text-xs text-indigo-600 hover:text-indigo-800">
                        View <i className="fa fa-external-link ml-0.5" />
                      </a>
                    )}
                    <button
                      onClick={() => handleDeleteJob(job.id)}
                      className="text-slate-400 hover:text-red-500 text-xs"
                      title="Remove listing"
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Applications tab ── */}
      {tab === "applications" && (
        <div className="space-y-4">

          {/* EEOC legal responsibility notice */}
          {eeocEnabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
              <span className="font-semibold">EEOC Self-ID Module — Your Responsibilities:</span>{" "}
              By enabling this module, you acknowledge that compliance with applicable EEOC, OFCCP,
              VEVRAA, and Section 503 obligations is your organization's responsibility. Velaro provides
              tools to facilitate voluntary self-identification data collection; we do not assume any
              compliance obligations on your behalf. If you are a federal contractor, consult employment
              counsel to confirm your use of this feature meets your OFCCP reporting requirements.
              Do not modify the federally prescribed disclosure language in your bot workflow.
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">
              {applications.length > 0
                ? `${applications.length} candidate${applications.length !== 1 ? "s" : ""} collected`
                : "No applications yet"}
            </div>
            {eeocEnabled && applications.length > 0 && (
              <a
                href="/api/messaging/Recruiting/Applications/ExportEeoc"
                download
                className="flex items-center gap-2 px-3 py-1.5 border border-slate-300 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50"
              >
                <i className="fa fa-download" />
                Export EEOC CSV
              </a>
            )}
          </div>

          {appsLoading ? (
            <LoadingSpinner />
          ) : applications.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <i className="fa fa-users text-3xl mb-3 block" />
              <p className="text-sm">No applications yet. Configure the bot wizard to start collecting leads.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {applications.map(app => (
                <div key={app.id} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="font-semibold text-slate-800">{app.candidateName || "Unknown"}</div>
                      <div className="text-sm text-slate-500">{app.email} {app.phone && `· ${app.phone}`}</div>
                    </div>
                    <div className="text-xs text-slate-400 flex-shrink-0">
                      {new Date(app.submittedAt).toLocaleString()}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {app.roleInterest && (
                      <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">
                        {app.roleInterest}
                      </span>
                    )}
                    {app.workAuthorization && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                        {app.workAuthorization}
                      </span>
                    )}
                    {app.availability && (
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">
                        Available: {app.availability}
                      </span>
                    )}
                  </div>

                  {app.notes && (
                    <p className="text-xs text-slate-500 mb-3 italic">{app.notes}</p>
                  )}

                  {/* EEOC data — only shown when module is enabled */}
                  {eeocEnabled && app.eeocOfferedAt && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        EEOC Voluntary Self-ID
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                        <div><span className="text-slate-400">Race/Ethnicity:</span> {app.eeocRaceEthnicity || "Not answered"}</div>
                        <div><span className="text-slate-400">Gender:</span> {app.eeocGender || "Not answered"}</div>
                        <div><span className="text-slate-400">Veteran status:</span> {app.eeocVeteranStatus || "Not answered"}</div>
                        <div><span className="text-slate-400">Disability:</span> {app.eeocDisabilityStatus || "Not answered"}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Activity log tab ── */}
      {tab === "log" && (
        <IntegrationActivityLog integrationSlug="Recruiting" />
      )}

      {/* Wizard */}
      {showWizard && (
        <StarterWorkflowWizard
          integration="recruiting"
          onClose={() => setShowWizard(false)}
        />
      )}
    </div>
  );
}
